import React, { useEffect } from "react";
import { Scan, AlertCircle, CheckCircle } from "lucide-react";
import { usePassportReader } from "../../hooks/usePassportReader";
import { PassportReaderStatusIndicator } from "./PassportReaderStatus";
import { PassportData } from "../../services/passportReader/passportReaderService";
import { PassportReaderStatus } from "../../services/passportReader/passportReaderService";

interface PassportScannerProps {
  onScanSuccess?: (data: PassportData) => void;
  onScanError?: (error: string) => void;
  autoConnect?: boolean;
  showStatus?: boolean;
}

export const PassportScanner: React.FC<PassportScannerProps> = ({
  onScanSuccess,
  onScanError,
  autoConnect = true,
  showStatus = true,
}) => {
  const {
    status,
    isReady,
    error,
    passportData,
    connect,
    disconnect,
    readPassport,
    clearError,
    clearPassportData,
  } = usePassportReader();

  useEffect(() => {
    if (autoConnect && status === PassportReaderStatus.DISCONNECTED) {
      connect();
    }

    return () => {
      if (autoConnect) {
        disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (passportData && onScanSuccess) {
      onScanSuccess(passportData);
      clearPassportData();
    }
  }, [passportData, onScanSuccess, clearPassportData]);

  useEffect(() => {
    if (error && onScanError) {
      onScanError(error);
    }
  }, [error, onScanError]);

  const handleScan = () => {
    if (!isReady) {
      console.warn("Passport reader not ready");
      return;
    }
    readPassport();
  };

  const isScanning = status === PassportReaderStatus.READING;

  return (
    <div className="space-y-4">
      {showStatus && (
        <PassportReaderStatusIndicator
          status={status}
          error={error}
          onConnect={connect}
          onDisconnect={disconnect}
        />
      )}

      <div className="flex flex-col items-center space-y-4 p-6 bg-slate-800 rounded-lg border border-slate-700">
        <div className="relative">
          <div
            className={`p-4 rounded-full ${isReady ? "bg-blue-900/20" : "bg-slate-700"}`}
          >
            <Scan
              className={`h-12 w-12 ${isReady ? "text-blue-500" : "text-slate-500"}`}
            />
          </div>
          {isScanning && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></div>
            </div>
          )}
        </div>

        <div className="text-center">
          <h3 className="text-lg font-medium text-white mb-2">
            {isScanning ? "Scanning Passport..." : "Scan Passport"}
          </h3>
          <p className="text-sm text-slate-400 mb-4">
            {isReady
              ? "Place the passport on the reader and click scan"
              : "Connect the passport reader to start scanning"}
          </p>
        </div>

        <button
          onClick={handleScan}
          disabled={!isReady || isScanning}
          className={`
            px-6 py-2 rounded-lg font-medium transition-all
            ${
              isReady && !isScanning
                ? "bg-blue-600 text-white hover:bg-blue-700 cursor-pointer"
                : "bg-slate-700 text-slate-500 cursor-not-allowed"
            }
          `}
        >
          {isScanning ? (
            <>
              <span className="inline-flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Scanning...
              </span>
            </>
          ) : (
            <>
              <Scan className="inline h-4 w-4 mr-2" />
              Start Scan
            </>
          )}
        </button>

        {error && (
          <div className="w-full p-3 bg-red-900/20 border border-red-600 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-400">{error}</p>
                <button
                  onClick={clearError}
                  className="mt-2 text-xs text-red-300 hover:text-red-200 underline"
                >
                  Clear error
                </button>
              </div>
            </div>
          </div>
        )}

        {passportData && (
          <div className="w-full p-3 bg-green-900/20 border border-green-600 rounded-lg">
            <div className="flex items-start space-x-2">
              <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-green-400">
                  Passport scanned successfully!
                </p>
                <p className="text-xs text-green-300 mt-1">
                  {passportData.fullName ||
                    `${passportData.firstName} ${passportData.lastName}`}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="p-3 bg-slate-800 rounded-lg border border-slate-700">
        <h4 className="text-sm font-medium text-slate-300 mb-2">
          Instructions:
        </h4>
        <ol className="text-xs text-slate-400 space-y-1 list-decimal list-inside">
          <li>
            Ensure the passport reader is connected to your computer via USB
          </li>
          <li>{`Wait for the device to initialize (status will show "Ready")`}</li>
          <li>
            Place the passport on the reader with the data page facing down
          </li>
          <li>{`Click "Start Scan" to read the passport information`}</li>
          <li>Keep the passport steady until scanning is complete</li>
        </ol>
      </div>
    </div>
  );
};
